import * as React from 'react'
import { cn } from '../../lib/utils'

/**
 * Toggle (switch) — native `<input type="checkbox" role="switch">` styled to the
 * TailGrids toggle model (the system our Figma is built on), wired to First Bite
 * tokens. Native input means free keyboard (Space), focus, and form semantics —
 * no extra dependency. Mirrors the {@link Checkbox} conventions:
 *
 *   size   sm | md          (h-5/w-9 · h-6/w-11 track)
 *   label  optional inline, clickable label (omit it → pass `aria-label`)
 *
 * Off = gray-200 track; on = basil-700 track (brand) + white thumb that slides
 * right; brand focus ring; disabled = gray-100 track + gray-300 thumb. Per the
 * TailGrids API, `className` lands on the input element — use `wrapperClassName`
 * for the overall control/label layout.
 */

type ToggleSize = 'sm' | 'md'

const trackSize: Record<ToggleSize, string> = {
  sm: 'h-5 w-9',
  md: 'h-6 w-11',
}

const thumbSize: Record<ToggleSize, string> = {
  sm: 'size-4 peer-checked:translate-x-4',
  md: 'size-5 peer-checked:translate-x-5',
}

export interface ToggleProps extends Omit<
  React.InputHTMLAttributes<HTMLInputElement>,
  'size' | 'type'
> {
  size?: ToggleSize
  /** Optional inline label, rendered in a clickable `<label>` wrapper. */
  label?: React.ReactNode
  /** Classes on the `<label>`/wrapper (width, alignment, gap). */
  wrapperClassName?: string
}

export const Toggle = React.forwardRef<HTMLInputElement, ToggleProps>(
  (
    { className, wrapperClassName, size = 'md', label, disabled, ...props },
    ref
  ) => {
    const control = (
      <span className={cn('relative inline-flex shrink-0', trackSize[size])}>
        <input
          ref={ref}
          type="checkbox"
          role="switch"
          disabled={disabled}
          className={cn(
            'peer absolute inset-0 z-10 m-0 cursor-pointer appearance-none rounded-full outline-none',
            'disabled:cursor-not-allowed',
            className
          )}
          {...props}
        />
        <span
          aria-hidden="true"
          className={cn(
            'pointer-events-none h-full w-full rounded-full bg-fbui-gray-200 transition-colors',
            'peer-enabled:peer-hover:bg-fbui-gray-300',
            'peer-checked:bg-fbui-basil-700 peer-checked:peer-enabled:peer-hover:bg-fbui-basil-800',
            'peer-disabled:bg-fbui-gray-100',
            'peer-focus-visible:ring-2 peer-focus-visible:ring-fbui-ring-brand'
          )}
        />
        <span
          aria-hidden="true"
          className={cn(
            'pointer-events-none absolute left-0.5 top-0.5 rounded-full bg-fbui-base-white shadow-sm transition-transform',
            'peer-disabled:bg-fbui-gray-300',
            thumbSize[size]
          )}
        />
      </span>
    )

    if (!label) {
      return wrapperClassName ? (
        <span className={cn('inline-flex', wrapperClassName)}>{control}</span>
      ) : (
        control
      )
    }

    return (
      <label
        className={cn(
          'inline-flex items-center gap-2.5 tracking-[-0.2px] select-none',
          size === 'sm' ? 'text-xs' : 'text-sm',
          disabled
            ? 'cursor-not-allowed text-fbui-text-disabled'
            : 'cursor-pointer text-fbui-gray-700',
          wrapperClassName
        )}
      >
        {control}
        <span>{label}</span>
      </label>
    )
  }
)
Toggle.displayName = 'Toggle'
